/**
 * Test data for the dev projects: production's orders of one delivery date, copied into dev with
 * nothing in them that can reach a customer (Bestellingen → Testdata, local and development only).
 *
 * The same rules as scripts/copy-orders/planOrders.mjs, with the test address and phone number
 * fixed instead of asked for:
 *  - every field whose whole value is an e-mail address becomes TEST_EMAIL, every phone number
 *    (a non-empty string under a key that says phone, mobile or telefoon) TEST_PHONE, and
 *    customer.email and customer.phoneNumber are set even where the order had none. An address
 *    inside a free text (a note) stays: nothing sends mail there;
 *  - the fields in which a carrier records that it has the order are left out: production's values
 *    point at real shipments, and the SooCool export skips an order that looks shipped already;
 *  - an order dev already has is left alone - empty the date first. A new one is created as
 *    CREATED, the only way the rules allow, and then given its production status.
 *
 * Pure: no Firestore, no React.
 */

export const TEST_EMAIL = "test@digitalkiss.nl";
export const TEST_PHONE = "0612345678";

/** Where a carrier export records that it has the order: SooCool (the api), Trunkrs, OptimoRoute. */
export const CARRIER_FIELDS = ["soocool", "trunkrsNumber", "optimoRouteExportedAt"];

/** REACT_APP_ENVIRONMENT of the builds that get the test data menu: `npm start` and build:dev. */
export const DEV_TOOLS_ENVIRONMENTS = ["local", "development"];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_KEY = /phone|mobile|telefoon/i;

const isPlainObject = (value) => value !== null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype;

/**
 * Whether this build may show the test data menu: a local or development build, with production's
 * Firebase config to read from (REACT_APP_PROD_*), and its own projects not production's.
 */
export const isDevToolsEnvironment = (env = process.env) => {
  if (!DEV_TOOLS_ENVIRONMENTS.includes(env.REACT_APP_ENVIRONMENT)) return false;
  return [
    [env.REACT_APP_VCE_PROJECTID, env.REACT_APP_PROD_VCE_PROJECTID],
    [env.REACT_APP_HVO_PROJECTID, env.REACT_APP_PROD_HVO_PROJECTID],
  ].every(([own, production]) => !!own && !!production && own !== production);
};

/**
 * The order with every e-mail address set to TEST_EMAIL and every phone number to TEST_PHONE.
 * Firestore's own types (Timestamp, a document reference) are no plain objects and pass unchanged.
 *
 * @returns {{ data: object, emails: number, phones: number }} the order and how many fields changed
 */
export const anonymiseOrder = (data) => {
  let emails = 0;
  let phones = 0;
  const walk = (value, key) => {
    if (typeof value === "string") {
      if (EMAIL.test(value.trim())) {
        if (value !== TEST_EMAIL) emails += 1;
        return TEST_EMAIL;
      }
      if (key && PHONE_KEY.test(key) && value.trim() !== "") {
        if (value !== TEST_PHONE) phones += 1;
        return TEST_PHONE;
      }
      return value;
    }
    if (Array.isArray(value)) return value.map((item) => walk(item, key));
    if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([name, item]) => [name, walk(item, name)]));
    return value;
  };
  const result = walk(data || {}, null);
  const customer = isPlainObject(result.customer) ? result.customer : {};
  if (customer.email !== TEST_EMAIL) emails += 1;
  if (customer.phoneNumber !== TEST_PHONE) phones += 1;
  result.customer = { ...customer, email: TEST_EMAIL, phoneNumber: TEST_PHONE };
  return { data: result, emails, phones };
};

/** The order without the carrier fields, and which of them it had. */
export const withoutCarrierFields = (data) => ({
  data: Object.fromEntries(Object.entries(data).filter(([key]) => !CARRIER_FIELDS.includes(key))),
  stripped: CARRIER_FIELDS.filter((field) => Object.prototype.hasOwnProperty.call(data, field)),
});

/**
 * @param {Array<{id: string, data: object}>} source - production's orders of the date
 * @param {Set<string>} existingIds - the ids of those orders that dev already has
 * @returns {Array<object>} per order: `id`, `orderId`, `status`, what was replaced (`emails`,
 *   `phones`, `stripped`) and `kind`: "create" with `data` (orderStatus CREATED) and `finalStatus`
 *   (the production status to set next, or null when that is CREATED), or "exists".
 */
export const planDevCopy = (source, existingIds) =>
  (source || []).map(({ id, data }) => {
    const base = { id, orderId: data?.orderId ?? null, status: data?.orderStatus ?? null };
    if (existingIds.has(id)) return { ...base, kind: "exists" };
    const anonymised = anonymiseOrder(data);
    const { data: desired, stripped } = withoutCarrierFields(anonymised.data);
    const status = desired.orderStatus;
    return {
      ...base,
      kind: "create",
      data: { ...desired, orderStatus: "CREATED" },
      finalStatus: status && status !== "CREATED" ? status : null,
      emails: anonymised.emails,
      phones: anonymised.phones,
      stripped,
    };
  });

/** "PAID 20, SHIPPED 3": how many orders of each status, the most common first. */
export const countByStatus = (entries) =>
  Object.entries(
    (entries || []).reduce((counts, entry) => {
      const status = entry.status || "geen";
      return { ...counts, [status]: (counts[status] || 0) + 1 };
    }, {}),
  )
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => `${status} ${count}`)
    .join(", ");

/**
 * What the SooCool export will make of these orders in dev: the products they refer to that dev
 * does not have, and how many of those it has lack a pick weight - both weigh nothing in a colli.
 *
 * @param {Array<{data: object}>} orders
 * @param {Array<object>} devProducts - this shop's products in dev, each with its `id`
 */
export const productCheck = (orders, devProducts) => {
  const ids = [
    ...new Set(
      (orders || []).flatMap(({ data }) =>
        (Array.isArray(data?.products) ? data.products : []).map((line) => line?.productId).filter((id) => typeof id === "string" && id),
      ),
    ),
  ];
  const byId = new Map((devProducts || []).map((product) => [product.id, product]));
  return {
    total: ids.length,
    missing: ids.filter((id) => !byId.has(id)),
    withoutPickWeight: ids.filter((id) => byId.has(id) && !(Number(byId.get(id).pickWeightGram) > 0)).length,
  };
};
